import Vue from 'vue'

const INSTALLED_KEY = 'selfbio.pwa.installed'
const DISMISSED_KEY = 'selfbio.pwa.dismissedUntil'
const WEEK = 7 * 24 * 60 * 60 * 1000
let deferredPrompt = null
let initialized = false

function read(key) {
  try { return window.localStorage.getItem(key) } catch (_) { return null }
}

function write(key, value) {
  try { window.localStorage.setItem(key, value) } catch (_) { /* Storage can be blocked in private browsing. */ }
}

export const pwaState = Vue.observable({
  ready: false,
  secure: false,
  standalone: false,
  installed: false,
  nativePrompt: false,
  platform: 'desktop',
  embedded: false,
  dialog: false,
  busy: false,
  message: '',
  dismissedUntil: 0,
})

export function dismissInstallOffer() {
  pwaState.dismissedUntil = Date.now() + WEEK
  write(DISMISSED_KEY, String(pwaState.dismissedUntil))
}

export function confirmInstalled() {
  pwaState.installed = true
  pwaState.nativePrompt = false
  pwaState.dialog = false
  deferredPrompt = null
  write(INSTALLED_KEY, '1')
}

export function resetInstallStatus() {
  if (pwaState.standalone) return
  write(INSTALLED_KEY, '0')
  pwaState.installed = false
  pwaState.message = ''
}

export function canOfferInstall() {
  return pwaState.ready && pwaState.secure && !pwaState.installed
    && (pwaState.nativePrompt || ['ios', 'android', 'mac-safari'].includes(pwaState.platform))
}

export function openInstallDialog() {
  pwaState.message = ''
  pwaState.dialog = true
}

export async function requestInstall() {
  if (pwaState.busy || pwaState.installed) return
  pwaState.message = ''
  if (!deferredPrompt) {
    pwaState.dialog = true

    return
  }
  const prompt = deferredPrompt
  deferredPrompt = null
  pwaState.nativePrompt = false
  pwaState.busy = true
  try {
    // Keep this call synchronous with the user's click; browsers require a user gesture.
    const result = await prompt.prompt()
    const choice = await (prompt.userChoice || result)
    if (choice && choice.outcome === 'accepted') {
      pwaState.message = 'Complete the installation in your browser, then open Self Biography from its app icon.'
    } else {
      pwaState.message = 'Installation was canceled. You can try again from your profile or your browser menu.'
    }
    dismissInstallOffer()
  } catch (_) {
    pwaState.message = 'The installation prompt could not open. Follow the browser instructions below or try again later.'
  } finally {
    pwaState.busy = false
  }
}

export function initializePwa() {
  if (initialized) return
  initialized = true
  const ua = navigator.userAgent || ''
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  pwaState.platform = ios ? 'ios' : 'desktop'
  if (!ios && /Android/i.test(ua)) pwaState.platform = 'android'
  if (!ios && /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua)) pwaState.platform = 'mac-safari'
  pwaState.embedded = /FBAN|FBAV|Instagram|; wv\)/i.test(ua)
  pwaState.secure = window.isSecureContext
  pwaState.dismissedUntil = Number(read(DISMISSED_KEY)) || 0
  const display = window.matchMedia('(display-mode: standalone)')
  const syncDisplay = () => {
    pwaState.standalone = display.matches || navigator.standalone === true
    pwaState.installed = pwaState.standalone || read(INSTALLED_KEY) === '1'
    if (pwaState.standalone) confirmInstalled()
  }
  syncDisplay()
  if (display.addEventListener) display.addEventListener('change', syncDisplay)
  else if (display.addListener) display.addListener(syncDisplay)
  window.addEventListener('pageshow', syncDisplay)
  window.addEventListener('storage', event => {
    if (event.key === INSTALLED_KEY || event.key === null) syncDisplay()
    if (event.key === DISMISSED_KEY || event.key === null) pwaState.dismissedUntil = Number(read(DISMISSED_KEY)) || 0
  })
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault()
    if (pwaState.standalone) return

    // A fresh install event is authoritative, including after the app was uninstalled.
    resetInstallStatus()
    deferredPrompt = event
    pwaState.nativePrompt = true
  })
  window.addEventListener('appinstalled', confirmInstalled)
  pwaState.ready = true

  if (process.env.NODE_ENV === 'production' && pwaState.secure && 'serviceWorker' in navigator) {
    const register = () => {
      navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
        .catch(error => console.warn('Offline support could not start:', error))
    }
    if (document.readyState === 'complete') register()
    else window.addEventListener('load', register, { once: true })
  }
}
